import { useCallback, useEffect, useRef, useState } from 'react';
import {
  EnvVarInput,
  RunAssistance,
  ServiceEnvironment,
} from '@back4app2/sdk';
import back4app2 from '../back4app2';
import { ReactComponent as CarretIcon } from '../assets/images/down-arrow.svg';
import { ReactComponent as CheckIcon } from '../assets/images/check-icon.svg';
import { ReactComponent as VisibilityIcon } from '../assets/images/visibility-icon.svg';
import { ReactComponent as VisibilityOffIcon } from '../assets/images/visibility-off-icon.svg';
import { DsButton, Eyebrow } from './ds';

/**
 * A section, as its own raised card.
 *
 * The system's surfaces do this work: page is navy-900, panels 800, raised
 * cards 700. Two groups asking for two different things sat as loose headings
 * on one flat surface, which is why the screen read as a single long form
 * rather than as "variables" and "configuration".
 *
 * Eyebrow for the title: Inter 500 12 / 0.14em / uppercase.
 */
const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <section className="rounded-card border border-navy-500 bg-navy-800 p-4 lg:p-5 mb-4">
    <Eyebrow className="block mb-4">{title}</Eyebrow>
    {children}
  </section>
);

/** One secret, one row, with the same show/hide control the Environment
 * Variables tab uses. A value typed here is a credential; a field that shows
 * it in the clear by default is a field people fill with someone looking. */
const SecretField = ({
  name,
  optional,
  value,
  onChange,
}: {
  name: string;
  optional?: boolean;
  value: string;
  onChange: (value: string) => void;
}) => {
  const [visible, setVisible] = useState(false);

  return (
    <div className="flex flex-wrap items-start gap-x-6 gap-y-2">
      {/* Just the name. The model's `purpose` was two lines under every
          variable, near-identical between them ("Required application
          configuration; the service exits if absent." twice), and three of
          those turned a short form into a wall. It stays on the scan row for
          support; what the customer needs here is which variable to fill. */}
      <div className="w-64 [@media(max-width:767.98px)]:w-full flex-none pt-2.5">
        <span className="font-courrier text-xs text-[#C5D0E0] break-all">
          {name}
        </span>
        {optional ? (
          <span className="ml-2 text-[10px] uppercase tracking-wide text-[#5E7493]">
            optional
          </span>
        ) : null}
      </div>
      <div className="flex-1 min-w-[16rem] [@media(max-width:767.98px)]:min-w-0 [@media(max-width:767.98px)]:w-full flex items-center gap-2 rounded-lg bg-[#111B2E] border border-[#283A58] px-3 py-2.5 focus-within:border-[#208AEC] focus-within:shadow-[0_0_0_3px_rgba(32,138,236,0.25)] transition-colors">
        <input
          type={visible ? 'text' : 'password'}
          autoComplete="off"
          placeholder={optional ? 'Optional' : 'Value'}
          className="flex-1 min-w-0 bg-transparent text-sm text-white placeholder:text-[#5E7493] outline-none"
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
        <button
          type="button"
          className="flex-none text-[#8FA2BD] hover:text-white transition-colors"
          aria-label={visible ? `Hide ${name}` : `Show ${name}`}
          onClick={() => setVisible(!visible)}
        >
          {visible ? <VisibilityOffIcon /> : <VisibilityIcon />}
        </button>
      </div>
    </div>
  );
};

/**
 * A field the panel offers a value for.
 *
 * The input starts EMPTY and stays that way unless someone fills it - the same
 * rule RuntimeVersionField follows. Prefilling with the model's answer would
 * look helpful and quietly turn a suggestion into configuration: the
 * repository influences the model, so a well-written README would end up
 * rewriting somebody's Start Command with nobody in between. Clicking "Use
 * this" IS that somebody.
 */
/**
 * A suggestion, with Apply ticked by default.
 *
 * Ticked-by-default is a product decision, and it moves the trust boundary
 * rather than removing it: the value is shown next to the one it replaces, and
 * nothing is written until the button below is pressed. What it must NOT do is
 * apply when nobody answered - the countdown still proceeds with no changes,
 * because an unattended screen is not consent (§5.3).
 */
const SuggestedRow = ({
  label,
  current,
  suggested,
  checked,
  onChange,
}: {
  label: string;
  current?: string;
  suggested: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) => (
  <label
    className={`flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border px-4 py-3 cursor-pointer transition-colors ${
      checked
        ? 'border-action bg-[#152B45]'
        : 'border-navy-500 bg-navy-700 hover:bg-navy-600'
    }`}
  >
    <span className="w-40 [@media(max-width:767.98px)]:w-full flex-none text-xs font-medium text-[#C5D0E0]">
      {label}
    </span>

    <span className="flex-1 min-w-[12rem] [@media(max-width:767.98px)]:min-w-0 [@media(max-width:767.98px)]:w-full flex items-baseline gap-2 flex-wrap">
      <span className="font-courrier text-[11px] text-[#5E7493] line-through break-all">
        {current || 'not set'}
      </span>
      <span className="text-[#5E7493] text-[11px]">→</span>
      <span className="font-courrier text-sm text-white break-all">
        {suggested}
      </span>
    </span>

    <span className="flex-none flex items-center gap-2">
      <span
        className={`text-ds-115 transition-colors ${
          checked ? 'text-action-soft' : 'text-navy-200'
        }`}
      >
        Apply
      </span>
      <span
        className={`h-4 w-4 rounded-sm border flex items-center justify-center transition-colors ${
          checked
            ? 'bg-action border-action'
            : 'border-navy-400 bg-transparent'
        }`}
      >
        {checked ? <CheckIcon className="w-3 h-3 text-white" /> : null}
      </span>
      <input
        type="checkbox"
        aria-label={`Apply ${label}`}
        className="sr-only"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
    </span>
  </label>
);

/**
 * What we understood about a project, and the last chance to correct it.
 *
 * One component, three hosts (§3.4): the creation wizard's last step, the
 * overview of an app whose first build is held, and the modal on the Deploy
 * button. They differ only in what "proceed" means - publishing a first build
 * or a redeploy - so that is the single prop that changes. A second copy of
 * this screen would drift from the first, and the drift would be invisible:
 * both would keep working, differently.
 *
 * Two kinds of field here, and the difference matters:
 *
 *   Environment variables are typed by the user. The model only suggested the
 *   NAME, so writing the value is safe.
 *
 *   Start command and port are model output. They are shown next to what is
 *   configured today and applied only on an explicit click. The repository
 *   influences the model, so letting model output become a Start Command with
 *   no human in between would make a well-written README a way to reconfigure
 *   somebody's app.
 */
const PreDeployPanel = ({
  appId,
  serviceEnvironment,
  runAssistance,
  proceedLabel = 'Save and deploy',
  skipLabel = 'Deploy without changes',
  autoProceedSeconds,
  onProceed,
  onCancel,
}: {
  appId: string;
  serviceEnvironment: ServiceEnvironment;
  /** Absent when the hold outlived the analysis that produced it. The panel
   * then has nothing to suggest and only offers to start the build. */
  runAssistance?: RunAssistance;
  proceedLabel?: string;
  skipLabel?: string;
  /** Seconds before the build starts on its own. Passed only by the hosts
   * that are HOLDING a build - the redeploy modal holds nothing, so a timer
   * there would deploy something the customer may have opened it to cancel. */
  autoProceedSeconds?: number;
  onProceed: () => Promise<void> | void;
  onCancel?: () => void;
}) => {
  const [values, setValues] = useState<Record<string, string>>({});
  // Ticked by default: the screen exists to offer these, and offering them
  // pre-declined made the common answer the one that took an extra click.
  const [applyStartCommand, setApplyStartCommand] = useState(true);
  const [applyPort, setApplyPort] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [secondsLeft, setSecondsLeft] = useState(autoProceedSeconds || 0);
  const [waiting, setWaiting] = useState(!!autoProceedSeconds);
  const [notesOpen, setNotesOpen] = useState(false);

  const configured = new Set(
    (serviceEnvironment.envVars || []).map((envVar) => envVar.key)
  );

  const missing = (runAssistance?.requiredEnvVars || []).filter(
    (envVar) => !configured.has(envVar.key)
  );

  const startCommandDiffers =
    !!runAssistance?.startCommand &&
    runAssistance.startCommand !== serviceEnvironment.startCommand;

  const portDiffers =
    !!runAssistance?.port &&
    runAssistance.port !== serviceEnvironment.exposedPort;

  const hasSomethingToApply =
    missing.length > 0 || startCommandDiffers || portDiffers;

  const filled = missing.filter((envVar) => (values[envVar.key] || '').trim());

  // What pressing the primary button would actually write. Nothing ticked and
  // nothing typed means nothing is saved — and a button that says "Save and
  // start" in that state is a promise the screen does not keep. The customer
  // presses it expecting the suggestion to be applied, the build starts
  // without it, and the value they came here for is nowhere.
  const willApply =
    filled.length > 0 ||
    (startCommandDiffers && applyStartCommand) ||
    (portDiffers && applyPort);

  // One line, in the header, next to the title: if the stack is wrong then
  // everything below it is wrong too, and that is worth a glance - not a row
  // of chips competing with the things the customer can act on.
  const stack = runAssistance?.stack
    ? `${runAssistance.stack}${
        runAssistance.runtimeVersion ? ` ${runAssistance.runtimeVersion}` : ''
      }`
    : undefined;

  const proceed = useCallback(
    async (withChanges: boolean) => {
      setWaiting(false);
      setSaving(true);
      setError(undefined);

      try {
        if (withChanges && willApply) {
          const envVars: EnvVarInput[] = [
            ...(serviceEnvironment.envVars || []).map((envVar) => ({
              key: envVar.key,
              value: envVar.value,
            })),
            ...filled.map((envVar) => ({
              key: envVar.key,
              value: values[envVar.key].trim(),
            })),
          ];

          await back4app2.updateAppSettings(
            appId,
            undefined,
            undefined,
            undefined,
            envVars,
            undefined,
            applyPort ? runAssistance?.port : undefined,
            applyStartCommand ? runAssistance?.startCommand : undefined
          );
        }

        await onProceed();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'we could not save that');
        setSaving(false);
      }
    },
    [
      appId,
      applyPort,
      applyStartCommand,
      filled,
      onProceed,
      runAssistance,
      serviceEnvironment,
      values,
      willApply,
    ]
  );

  /**
   * Stops the countdown, for good.
   *
   * Any interaction with the form cancels it: someone halfway through typing a
   * secret must never have the build start under them. It does not restart -
   * a timer that resurrects after the customer engaged would be worse than no
   * timer, because by then they are relying on it not to fire.
   */
  const stopWaiting = useCallback(() => setWaiting(false), []);

  const proceedRef = useRef(proceed);
  proceedRef.current = proceed;

  useEffect(() => {
    if (!waiting || saving) {
      return;
    }

    if (secondsLeft <= 0) {
      // Nothing was chosen, so nothing is applied - `false`, deliberately.
      proceedRef.current(false);
      return;
    }

    const timer = setTimeout(() => setSecondsLeft((left) => left - 1), 1000);

    return () => clearTimeout(timer);
  }, [waiting, saving, secondsLeft]);

  return (
    <div className="font-inter text-white w-full">
      {/* No title here: the card that hosts this panel already carries one,
          and two "Before we build" one under the other is what the last
          revision shipped. */}
      <p className="text-sm text-[#8FA2BD] leading-[1.55] mb-6">
        {hasSomethingToApply ? (
          <>
            We read this as {stack ? <strong className="text-white">{stack}</strong> : 'a web app'}.
            Nothing below is required — you can change all of it later in
            Settings.
          </>
        ) : (
          <>
            We read this as {stack ? <strong className="text-white">{stack}</strong> : 'a web app'},
            and found nothing to change.
          </>
        )}
      </p>

      {missing.length ? (
        <Section title="Environment variables">
          <div className="space-y-3">
            {missing.map((envVar) => (
              <SecretField
                key={envVar.key}
                name={envVar.key}
                optional={envVar.required === false}
                value={values[envVar.key] || ''}
                onChange={(next) => {
                  stopWaiting();
                  setValues({ ...values, [envVar.key]: next });
                }}
              />
            ))}
          </div>
        </Section>
      ) : null}

      {startCommandDiffers || portDiffers ? (
        <Section title="Suggested configuration">
          <div className="space-y-3">
            {startCommandDiffers ? (
              <SuggestedRow
                label="Start command"
                current={serviceEnvironment.startCommand}
                suggested={runAssistance!.startCommand as string}
                checked={applyStartCommand}
                onChange={(next) => {
                  stopWaiting();
                  setApplyStartCommand(next);
                }}
              />
            ) : null}

            {portDiffers ? (
              <SuggestedRow
                label="Port"
                current={
                  serviceEnvironment.exposedPort
                    ? String(serviceEnvironment.exposedPort)
                    : undefined
                }
                suggested={String(runAssistance!.port)}
                checked={applyPort}
                onChange={(next) => {
                  stopWaiting();
                  setApplyPort(next);
                }}
              />
            ) : null}
          </div>
        </Section>
      ) : null}

      {/* Folded away by default. These notes are prose the panel cannot act
          on - useful when a build fails, noise while deciding whether to
          click a checkbox, and three grey lines of it drown everything the
          customer actually has to answer. */}
      {runAssistance?.runNotes ? (
        <section className="mb-6 px-1">
          <button
            type="button"
            className="flex items-center gap-2 text-[11px] text-[#8FA2BD] hover:text-white transition-colors"
            onClick={() => setNotesOpen(!notesOpen)}
          >
            <CarretIcon
              className={`w-3 transition-transform duration-300 ${
                notesOpen ? 'rotate-180' : ''
              }`}
            />
            What else we noticed
          </button>
          {notesOpen ? (
            <p className="text-[11px] leading-[1.5] text-[#8FA2BD] mt-3 pl-5">
              {runAssistance.runNotes}
            </p>
          ) : null}
        </section>
      ) : null}

      {error ? (
        <p className="text-sm text-[#E85C3E] leading-[1.55] mb-4">{error}</p>
      ) : null}

      {/* Stacks on a phone — status, Cancel, then the two actions one over the
          other and full width. A `mr-auto` Cancel wrapping away from an
          `ml-auto` button group left it stranded on a line of its own.

          The two actions were side by side there too. Their labels do not
          wrap, and "Deploy without changes" beside "Save and deploy" is wider
          than a phone's panel: the second one ran off the edge and the modal
          scrolled sideways to reach it. */}
      <div className="flex flex-col gap-3 pt-5 border-t border-navy-500 sm:flex-row sm:flex-wrap sm:items-center">
        {/* In the action bar, not above it: it is a statement about the
            buttons - what happens if you press none of them. */}
        {waiting && !saving ? (
          <span className="flex items-center gap-2 text-ds-115 text-navy-200 sm:mr-auto">
            <span className="h-1.5 w-1.5 rounded-full bg-action animate-pulse flex-none" />
            Starting on its own in {secondsLeft}s
          </span>
        ) : autoProceedSeconds && !saving ? (
          /* The countdown was cancelled by an interaction, and stays
             cancelled. Saying so matters: the line simply vanishing reads as
             a timer that broke, and someone can sit here waiting for a build
             that is now waiting for them. */
          <span className="text-ds-115 text-navy-300 sm:mr-auto">
            Waiting for you — nothing starts on its own now.
          </span>
        ) : null}
        {onCancel ? (
          <button
            type="button"
            className="b4-focus self-start rounded-control px-1 text-ds-135 text-navy-200 hover:text-white transition-colors sm:mr-auto"
            onClick={onCancel}
            disabled={saving}
          >
            Cancel
          </button>
        ) : null}
        {/* DsButton rather than the shared one. That button blooms a 10px drop
            shadow and changes font-weight on hover, so it visibly MOVES —
            holding it still used to take three `!important` overrides right
            here. This one does not move. */}
        <span className="flex flex-col gap-3 sm:flex-row sm:items-center sm:ml-auto">
          {/* Never a gate. The detection can be wrong, and an app may get its
              configuration from somewhere we cannot see. */}
          {willApply ? (
            <DsButton
              variant="outline"
              size="touch"
              className="sm:!h-[38px]"
              isDisabled={saving}
              onClick={() => proceed(false)}
            >
              {skipLabel}
            </DsButton>
          ) : null}
          <DsButton
            variant="primary"
            size="touch"
            className="sm:!h-[38px]"
            isDisabled={saving}
            onClick={() => proceed(true)}
          >
            {saving
              ? 'Starting…'
              : willApply
              ? proceedLabel
              : 'Start the build'}
          </DsButton>
        </span>
      </div>
    </div>
  );
};

export default PreDeployPanel;
